import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from './utils/Toast'; // Assuming you have this
import Loader from './utils/Loader'; // Assuming you have this
import Navbar from './utils/Navbr';  // Assuming you have this
import './style/Billing.css';

const Billing = () => {
    const navigate = useNavigate();

    // --- State ---
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [user, setUser] = useState(null);
    
    // Data State
    const [balance, setBalance] = useState(0.00);
    const [minW, setMinW] = useState(0.00);
    const [withdrawHistory, setWithdrawHistory] = useState([]);

    // Form State
    const [amount, setAmount] = useState('');
    const [upiId, setUpiId] = useState('');

    // --- Helper: Get Cookie ---
    const getCookie = (name) => {
        const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
        return match ? decodeURIComponent(match[1]) : null;
    };

    // --- Load Data & Auth ---
    const fetchData = async () => {
        const authToken = getCookie('authToken');
        
        try {
            // 1. Auth Check
            const authRes = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'checkAuth',
                authToken
            });

            if (authRes.data.status === false) {
                toast.error(authRes.data.message || 'Session expired');
                navigate('/login');
                return;
            }
            setUser(authRes.data.res);

            // 2. Fetch Billing Data (Balance + History)
            const billingRes = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'getBillingData', 
                authToken
            });

            if (billingRes.data.status) {
                setBalance(parseFloat(billingRes.data.balance || 0));
                setMinW(parseFloat(billingRes.data.minW || 0));
                setWithdrawHistory(billingRes.data.history || []);
            }

        } catch (error) {
            console.error(error);
            toast.error('Failed to load billing data');
        } finally {
            setLoading(false);
        }
    };

    // --- Effect: Mount ---
    useEffect(() => {
        // Load saved UPI from LocalStorage
        const savedUpi = localStorage.getItem('publisher_upi_id');
        if (savedUpi) {
            setUpiId(savedUpi);
        }
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- Handlers ---
    const handleWithdraw = async (e) => {
        e.preventDefault();
        
        // Validation
        if (!amount || parseFloat(amount) <= 0) {
            toast.error('Please enter a valid amount');
            return;
        }
        if (parseFloat(amount) > balance) {
            toast.error('Insufficient balance');
            return;
        }
        if (!upiId || upiId.length < 3) {
            toast.error('Please enter a valid UPI ID');
            return;
        }

        const authToken = getCookie('authToken');
        setSubmitting(true);

        try {
            const res = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'requestWithdrawal',
                authToken,
                amount: parseFloat(amount),
                upi: upiId
            });

            if (res.data.status) {
                toast.success('Withdrawal request submitted!');
                
                // Save UPI to LocalStorage for next time
                localStorage.setItem('publisher_upi_id', upiId);
                
                // Reset form and refresh data
                setAmount('');
                fetchData(); 
            } else {
                toast.error(res.data.message || 'Request failed');
            }
        } catch (error) {
            console.error(error);
            toast.error('Server error during withdrawal');
        } finally {
            setSubmitting(false);
        }
    };

    const copyTxnId = (id) => {
        navigator.clipboard.writeText(id);
        toast.success(`Copied: ${id}`);
    };

    // --- Render Helpers ---
    const getStatusClass = (status) => {
        switch(status.toLowerCase()) {
            case 'approved': return 'status-success-bill';
            case 'pending': return 'status-pending-bill';
            case 'rejected': return 'status-rejected-bill';
            default: return '';
        }
    };

    if (loading) return <Loader />;

    return (
        <div className="billing-page-wrapper-bill">
            {/* Background Blobs (Reusing style logic but scoped) */}
            <div className="bg-shape-bill shape-b1"></div>
            <div className="bg-shape-bill shape-b2"></div>

            <Navbar name={user?.name} role={user?.role} />

            <div className="billing-container-bill">
                
                <div className="billing-header-bill">
                    <h2>Billing & Payouts</h2>
                    <p>Manage your earnings and withdraw funds directly.</p>
                </div>

                {/* --- Top Section: Balance & Withdraw Form --- */}
                <div className="billing-grid-bill">
                    
                    {/* Balance Card */}
                    <div className="balance-card-bill">
                        <div className="balance-label">Available Balance</div>
                        <div className="balance-amount">₹{balance.toFixed(2)}</div>
                        <div className="balance-sub">Minimum withdrawal: ₹{minW.toFixed(2)}</div>
                        <div className="wallet-icon-bg">💰</div>
                    </div>

                    {/* Withdraw Form Card */}
                    <div className="withdraw-card-bill">
                        <div className="card-title-bill">Request Withdrawal</div>
                        <form onSubmit={handleWithdraw}>
                            <div className="form-row-bill">
                                {/* Amount Input */}
                                <div className="input-group-bill">
                                    <label>Amount ($)</label>
                                    <div className="input-wrapper-bill">
                                        <span className="input-icon-bill">₹</span>
                                        <input 
                                            type="number" 
                                            placeholder="0.00" 
                                            value={amount}
                                            onChange={(e) => setAmount(e.target.value)}
                                            step="0.01"
                                            min="0"
                                        />
                                    </div>
                                </div>

                                {/* UPI Input */}
                                <div className="input-group-bill">
                                    <label>UPI ID</label>
                                    <div className="input-wrapper-bill">
                                        <span className="input-icon-bill">🏦</span>
                                        <input 
                                            type="text" 
                                            placeholder="username@upi" 
                                            value={upiId}
                                            onChange={(e) => setUpiId(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <button 
                                type="submit" 
                                className={`withdraw-btn-bill ${submitting ? 'disabled' : ''}`}
                                disabled={submitting}
                            >
                                {submitting ? 'Processing...' : 'WITHDRAW FUNDS 🚀'}
                            </button>
                        </form>
                    </div>
                </div>

                {/* --- Bottom Section: History Table --- */}
                <div className="history-card-bill">
                    <div className="card-header-row-bill">
                        <h3>Withdrawal History</h3>
                        <button className="refresh-mini-bill" onClick={fetchData} title="Refresh History">🔄</button>
                    </div>
                    
                    <div className="table-responsive-bill">
                        <table className="billing-table-bill">
                            <thead>
                                <tr>
                                    <th>Txn ID</th>
                                    <th>Date & Time</th>
                                    <th>UPI ID</th>
                                    <th className="text-right">Amount</th>
                                    <th className="text-right">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {withdrawHistory.length > 0 ? (
                                    withdrawHistory.map((txn, index) => (
                                        <tr key={index}>
                                            <td>
                                                <span 
                                                    className="txn-id-pill" 
                                                    onClick={() => copyTxnId(txn.txn_id)}
                                                    title="Click to copy"
                                                >
                                                    {txn.txn_id} 📋
                                                </span>
                                            </td>
                                            <td className="date-cell">{txn.time}</td>
                                            <td className="upi-cell">{txn.upi}</td>
                                            <td className="text-right amount-text">₹{parseFloat(txn.amount).toFixed(2)}</td>
                                            <td className="text-right">
                                                <span className={`status-badge-bill ${getStatusClass(txn.status)}`}>
                                                    {txn.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="no-data-bill">No withdrawal history found.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default Billing;